import { Button, ButtonGroup, Classes, Colors, Dialog, Icon, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import Moment from "moment";
import {
  ButtonContainer,
  FieldSet,
  IDataTableColumn,
  showError,
  showSuccess,
  useGridState,
  useSearchApi,
} from "nsitools-react";
import * as React from "react";

import { DogHealthSearch, DogHealthTestApi, DogHealthTestGridDto } from "../../../../../api";
import { AddButton, CustomDataTable, DeleteButton, ExportButton } from "../../../../../components";
import { useDialog, useGlobalData } from "../../../../../contexts";
import { useApiService, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { DogHealthTestPanel } from "./DogHealthTestPanel";

export interface IDogHealthTestListProps {
  dogId: number;
  editMode: boolean;
}

export const DogHealthTestList: React.FunctionComponent<IDogHealthTestListProps> = ({ dogId, editMode }) => {
  const { t } = useTl();
  const api = useApiService(DogHealthTestApi);
  const { showDialogPromise } = useDialog();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const { currentLanguage } = useGlobalData();
  const [sobjState, setsobjState] = React.useState<any>();
  const [invalidatingRequests, setinvalidatingRequests] = React.useState([]);

  const tableState = useGridState({
    serverMode: true,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: { createdOn: "DESC" },
  });

  const searchFunction = React.useCallback(
    (sObj: DogHealthSearch) => {
      setsobjState(
        DogHealthSearch.fromJS({
          ...sObj,
          dogId: dogId,
          languageId: currentLanguage.languageId,
        })
      );
      return api.baseSearch(
        DogHealthSearch.fromJS({
          ...sObj,
          dogId: dogId,
          languageId: currentLanguage.languageId,
        })
      );
    },
    [api, currentLanguage.languageId, dogId]
  );

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const invalidateRequest = React.useCallback(
    async (dogCharacteristicValueId: number) => {
      const dialogResult = await showDialogPromise({
        message: t(ETLCodes.YouWantToInvalidateTheResultConfirmation),
        title: t(ETLCodes.Warning),
      });
      if (dialogResult !== "yes") {
        return;
      }
      invalidatingRequests.push(dogCharacteristicValueId);
      setinvalidatingRequests([...invalidatingRequests]);
      try {
        await api.invalidateDogCharacteristicsValue(dogCharacteristicValueId);
        showSuccess(t(ETLCodes.SaveSuccess));
        search();
      } catch {
        showError(t(ETLCodes.Error));
      }
      setinvalidatingRequests(invalidatingRequests.filter((c) => c !== dogCharacteristicValueId));
    },
    [api, invalidatingRequests, search, showDialogPromise, t]
  );

  const deleteCallback = React.useCallback(
    async (dogCharacteristicValueId: number) => {
      const dialogResult = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage),
        title: t(ETLCodes.Warning),
      });
      if (dialogResult !== "yes") {
        return;
      }
      try {
        await api.delete(dogCharacteristicValueId);
        showSuccess(t(ETLCodes.DeleteSuccess));
        search();
      } catch {
        showError(t(ETLCodes.Error));
      }
    },
    [api, search, showDialogPromise, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: DogHealthTestGridDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deleteCallback(row.dogCharacteristicValueId)} />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Characteristic),
        fieldName: "characteristic",
        disableSorting: true,
      },
      {
        header: () => t(ETLCodes.CreatedOn),
        fieldName: "createdOn",
        disableSorting: true,
        computed: true,
      },
      {
        header: () => t(ETLCodes.Value),
        fieldName: "value",
        render: (row: DogHealthTestGridDto) => <div>{row.value ?? row.numericValue}</div>,
      },
      {
        header: () => t(ETLCodes.TestType),
        fieldName: "test",
        render: (row: DogHealthTestGridDto) => {
          if (row.characteristicType.includes("03")) {
            return <Icon icon={IconNames.EYE_OPEN} />;
          } else if (row.characteristicType.includes("04")) {
            return <Icon icon={IconNames.HEART} />;
          } else if (row.characteristicType.includes("09")) {
            return <Icon icon={IconNames.CHART} />;
          }
        },
      },
      {
        header: () => t(ETLCodes.invalidatedOn),
        fieldName: "invalidatedOn",
        render: (row: DogHealthTestGridDto) => {
          console.log("invalidated on", row.invalidatedOn);
          if (!row.invalidatedOn) {
            return (
              <Button
                onClick={() => invalidateRequest(row.dogCharacteristicValueId)}
                text={t(ETLCodes.InvalidateRequest)}
                loading={invalidatingRequests.find((c) => c === row.dogCharacteristicValueId) > 0}
              />
            );
          } else {
            return <div>{Moment(row.invalidatedOn).format("DD/MM/yyyy")}</div>;
          }
        },
      },
    ],
    [deleteCallback, editMode, invalidateRequest, invalidatingRequests, t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);

  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.HealthTests)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode && (
              <AddButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction();
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>

      <Dialog
        title={t(ETLCodes.Characteristics)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <DogHealthTestPanel
            dogId={dogId}
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
